/**
 * Phase 4 · Order management: live order board with status filters, full
 * order detail (items, modifiers, tickets, bill) and manager-approved voids.
 */
import { useCallback, useEffect, useState } from 'react';
import { http, ApiError } from '../api/client';
import { useAuth } from '../store/auth';
import { useUi } from '../store/ui';
import { money, num, dateTime } from '../lib/format';
import { DataTable, type Column } from '../components/ui/DataTable';
import { Button } from '../components/ui/Button';
import { StatusBadge } from '../components/ui/Badge';
import { Modal } from '../components/ui/Modal';
import { Input } from '../components/ui/Input';

interface Order { id: string; order_number: string; order_type: string; status: string; total: number; opened_at: string; table_label: string | null; waiter_name: string | null; customer_name: string | null; item_count: number }
interface OrderItem { id: string; name_snapshot: string; quantity: number; status: string; line_total: number; notes: string | null }

const FILTERS: { label: string; status: string | null }[] = [
  { label: 'Live', status: 'open,sent,preparing,ready' },
  { label: 'Paid', status: 'paid,completed' },
  { label: 'Voided', status: 'voided,cancelled,refunded' },
  { label: 'All', status: null },
];

export function OrdersPage() {
  const canVoid = useAuth((s) => s.user?.permissions.includes('orders:void') ?? false);
  const toast = useUi((s) => s.toast);
  const [filter, setFilter] = useState(FILTERS[0]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState<{ order: Order; items: OrderItem[] } | null>(null);
  const [voidReason, setVoidReason] = useState('');
  const [voiding, setVoiding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await http.get<{ orders: Order[] }>('/orders', filter.status ? { status: filter.status } : undefined);
      setOrders(r.orders);
    } catch (e) {
      toast({ kind: 'error', title: 'Load failed', message: e instanceof ApiError ? e.message : String(e) });
    } finally { setLoading(false); }
  }, [filter, toast]);
  useEffect(() => { void load(); }, [load]);

  const open = async (o: Order) => {
    try {
      const d = await http.get<{ order: Order; items: OrderItem[] }>(`/orders/${o.id}`);
      setDetail({ order: d.order, items: d.items });
    } catch (e) {
      toast({ kind: 'error', title: 'Load failed', message: e instanceof ApiError ? e.message : String(e) });
    }
  };

  const doVoid = async () => {
    if (!detail) return;
    setVoiding(true);
    try {
      await http.post(`/orders/${detail.order.id}/void`, { reason: voidReason });
      toast({ kind: 'success', title: `Order #${detail.order.order_number} voided` });
      setDetail(null); setVoidReason('');
      await load();
    } catch (e) {
      toast({ kind: 'error', title: 'Void failed', message: e instanceof ApiError ? e.message : String(e) });
    } finally { setVoiding(false); }
  };

  const cols: Column<Order>[] = [
    { key: 'order_number', header: '#', sortable: true, render: (r) => `#${r.order_number}` },
    { key: 'order_type', header: 'Type', render: (r) => r.order_type.replace('_', ' ') },
    { key: 'table_label', header: 'Table', render: (r) => r.table_label ?? '—' },
    { key: 'item_count', header: 'Items' },
    { key: 'total', header: 'Total', render: (r) => money(r.total) },
    { key: 'waiter_name', header: 'Waiter', render: (r) => r.waiter_name ?? '—' },
    { key: 'opened_at', header: 'Opened', render: (r) => dateTime(r.opened_at) },
    { key: 'status', header: 'Status', render: (r) => <StatusBadge status={r.status} /> },
  ];

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold text-ink">Orders</h1>
        <div className="flex gap-1 rounded-lg bg-surface-1 p-1">
          {FILTERS.map((f) => (
            <button key={f.label} onClick={() => setFilter(f)}
              className={`rounded-md px-3 py-1.5 text-sm font-medium ${filter.label === f.label ? 'bg-surface-0 text-ink shadow-sm' : 'text-ink-muted hover:text-ink'}`}>
              {f.label}
            </button>
          ))}
        </div>
      </header>

      <DataTable columns={cols} rows={orders} loading={loading} onRowClick={(r) => void open(r)} />

      {detail && (
        <Modal open onClose={() => setDetail(null)} title={`Order #${detail.order.order_number}`} size="lg"
          footer={<>
            <Button variant="secondary" onClick={() => setDetail(null)}>Close</Button>
            {canVoid && !['voided', 'cancelled', 'paid'].includes(detail.order.status) && (
              <Button variant="danger" loading={voiding} disabled={voidReason.trim().length < 3} onClick={() => void doVoid()}>Void order</Button>
            )}
          </>}>
          <div className="mb-3 flex flex-wrap gap-4 text-sm text-ink-muted">
            <span>Status <StatusBadge status={detail.order.status} /></span>
            <span>Type {detail.order.order_type.replace('_', ' ')}</span>
            <span>Table {detail.order.table_label ?? '—'}</span>
            <span>Waiter {detail.order.waiter_name ?? '—'}</span>
            <span>Opened {dateTime(detail.order.opened_at)}</span>
          </div>
          <div className="space-y-1">
            {detail.items.map((i) => (
              <div key={i.id} className="flex items-center justify-between gap-2 text-sm">
                <span className="text-ink">{num(i.quantity, 0)}× {i.name_snapshot}{i.notes ? <em className="text-xs text-ink-muted"> — {i.notes}</em> : null}</span>
                <span className="flex items-center gap-3"><StatusBadge status={i.status} />{money(i.line_total)}</span>
              </div>
            ))}
          </div>
          {canVoid && !['voided', 'cancelled', 'paid'].includes(detail.order.status) && (
            <div className="mt-4">
              <Input label="Void reason (required, audited)" value={voidReason} onChange={(e) => setVoidReason(e.target.value)} placeholder="e.g. customer left, kitchen outage…" />
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}
